import { Preferences } from "@capacitor/preferences";
import type { PersistedEnvelope } from "../types";
import type { StorageBackend } from "./StorageBackend";

/** Prefix for all Capacitor Preferences keys used by persisted slices. */
const KEY_PREFIX = "flowride.persist.";

/**
 * Returns whether a Preferences key belongs to this persistence module.
 *
 * @param key Full key from `Preferences.keys()`.
 */
const isFlowridePersistKey = ( key: string ): boolean => key.startsWith( KEY_PREFIX );

/**
 * Strips {@link KEY_PREFIX} to return a slice registry key.
 *
 * @param key Full Preferences key.
 */
const toSliceKeyFromPreferencesKey = ( key: string ): string => key.slice( KEY_PREFIX.length );

/**
 * Capacitor Preferences backend for non-sensitive slices.
 *
 * @remarks Uses native KV on device; on web dev builds falls back to `localStorage`.
 */
export const preferencesBackend: StorageBackend = {
    "kind": "preferences",

    /**
     * Loads and JSON-parses an envelope from Preferences.
     *
     * @typeParam T Expected `data` shape after parse.
     * @param key Slice key (prefix applied internally).
     */
    async get<T>( key: string ): Promise<PersistedEnvelope<T> | null> {
        const raw = await Preferences.get( { "key": `${ KEY_PREFIX }${ key }` } );
        if ( raw.value == null ) {
            return null;
        }

        try {
            return JSON.parse( raw.value ) as PersistedEnvelope<T>;
        } catch( error: unknown ) {
            console.error( `[PreferencesBackend] Failed to parse envelope for ${ key }`, error );

            return null;
        }
    },

    /**
     * Serializes and stores an envelope under the prefixed key.
     *
     * @typeParam T Envelope `data` type.
     * @param key Slice key.
     * @param envelope Versioned blob to persist.
     */
    async set<T>( key: string, envelope: PersistedEnvelope<T> ): Promise<void> {
        await Preferences.set( {
            "key":   `${ KEY_PREFIX }${ key }`,
            "value": JSON.stringify( envelope ),
        } );
    },

    /**
     * Removes the prefixed Preferences entry for a slice.
     *
     * @param key Slice key.
     */
    async del( key: string ): Promise<void> {
        await Preferences.remove( { "key": `${ KEY_PREFIX }${ key }` } );
    },

    /**
     * Returns slice keys for all entries under {@link KEY_PREFIX}.
     */
    async keys(): Promise<string[]> {
        const { keys } = await Preferences.keys();

        return keys.filter( isFlowridePersistKey ).map( toSliceKeyFromPreferencesKey );
    },
};
